import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  Typography,
  Divider,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Chip,
  Alert,
  useTheme,
  useMediaQuery,
  Stack,
  Paper,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  CreditCard as CardIcon,
  ViewModule as TemplateIcon,
  Domain as DomainIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Payment as PaymentIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Email as EmailIcon,
  Warning as WarningIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';
import { useSubscription } from '../../hooks/useSubscription';

const drawerWidth = 280;

const DashboardLayout = ({ children }) => {
  const theme = useTheme();
  const { isDark, toggleTheme } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isEmailVerified } = useAuth();
  const { isActive } = useSubscription();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const navigation = [
    {
      name: 'Overview',
      href: '/dashboard',
      icon: <DashboardIcon />,
      category: 'main',
    },
    {
      name: 'Mis Tarjetas',
      href: '/cards',
      icon: <CardIcon />,
      requiresVerification: true,
      category: 'main',
    },
    {
      name: 'Perfiles',
      href: '/profiles',
      icon: <AccountIcon />,
      requiresVerification: true,
      category: 'main',
    },
    {
      name: 'Plantillas',
      href: '/templates',
      icon: <TemplateIcon />,
      category: 'main',
    },
    // ...(isActive ? [{
    //   name: 'Subdominios',
    //   href: '/subdomains',
    //   icon: <DomainIcon />,
    //   category: 'main',
    // }] : []),
    // {
    //   name: 'Analytics',
    //   href: '/analytics',
    //   icon: <AnalyticsIcon />,
    //   category: 'main',
    // },
    // {
    //   name: 'Configuración',
    //   href: '/settings',
    //   icon: <SettingsIcon />,
    //   category: 'general',
    // },
    // {
    //   name: 'Suscripción',
    //   href: '/payment',
    //   icon: <PaymentIcon />,
    //   category: 'general',
    // },
    {
      name: 'Notificaciones',
      href: '/notifications',
      icon: <NotificationsIcon />,
      category: 'general',
    },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    await logout();
    navigate('/login');
  };

  const isActiveRoute = (href) => {
    // Activa también para rutas hijas, p.ej. /cards/* debe activar /cards
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  const getPageTitle = () => {
    // Busca por coincidencia exacta o prefijo
    const currentPage = navigation.find(item => isActiveRoute(item.href));
    return currentPage?.name || 'Dashboard';
  };

  const drawer = (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: isDark ? '#0f0f0f' : '#f8fafc',
    }}>
      {/* Header del Sidebar */}
      <Box sx={{ p: 3, pb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box component={Link} to="/dashboard" sx={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
              <Box component="img" src="/icons/logo-horizontal.svg" alt="CardQr" sx={{ height: 38 }} />
            </Box>
          </Stack>
          {isMobile && (
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' }
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Navigation */}
      <Box sx={{ px: 3, flexGrow: 1 }}>
        {/* Main Navigation */}
        <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.75rem' }}>
          Dashboards
        </Typography>
        <List sx={{ mb: 3 }}>
          {navigation.filter(item => item.category === 'main').map((item) => {
            const isDisabled = item.requiresVerification && !isEmailVerified;
            const isActive = isActiveRoute(item.href);

            return (
              <ListItem key={item.name} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to={item.href}
                  disabled={isDisabled}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    px: 2,
                    backgroundColor: isActive ? 'primary.main' : 'transparent',
                    color: isActive ? 'white' : 'text.primary',
                    '&:hover': {
                      backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                    },
                    '&.Mui-disabled': {
                      color: 'text.disabled',
                      backgroundColor: 'transparent',
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive ? 'white' : isDisabled ? 'text.disabled' : 'text.secondary',
                      minWidth: 36
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: isActive ? 600 : 500,
                        fontSize: '0.875rem',
                      }
                    }}
                  />
                  {isDisabled && (
                    <EmailIcon sx={{ color: 'warning.main', fontSize: 16, ml: 1 }} />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        {/* General Navigation */}
        <Typography variant="overline" color="text.secondary" sx={{ mb: 1, fontSize: '0.75rem' }}>
          General
        </Typography>
        <List>
          {navigation.filter(item => item.category === 'general').map((item) => {
            const isDisabled = item.requiresVerification && !isEmailVerified;
            const isActive = isActiveRoute(item.href);

            return (
              <ListItem key={item.name} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to={item.href}
                  disabled={isDisabled}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    px: 2,
                    backgroundColor: isActive ? 'primary.main' : 'transparent',
                    color: isActive ? 'white' : 'text.primary',
                    '&:hover': {
                      backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                    },
                    '&.Mui-disabled': {
                      color: 'text.disabled',
                      backgroundColor: 'transparent',
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive ? 'white' : isDisabled ? 'text.disabled' : 'text.secondary',
                      minWidth: 36
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: isActive ? 600 : 500,
                        fontSize: '0.875rem',
                      }
                    }}
                  />
                  {isDisabled && (
                    <EmailIcon sx={{ color: 'warning.main', fontSize: 16, ml: 1 }} />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Footer Actions */}
      <Box sx={{ p: 3, pt: 2 }}>
        <Stack spacing={2}>
          {/* Plan Status */}
          <Box sx={{ textAlign: 'center' }}>
            <Chip
              label={user?.plan === 'premium' ? 'Premium Plan' : 'Free Plan'}
              size="small"
              color={user?.plan === 'premium' ? 'primary' : 'default'}
              sx={{
                borderRadius: 1.5,
                fontWeight: 500,
                fontSize: '0.75rem'
              }}
            />
          </Box>

          {/* Theme Toggle */}
          <Stack direction="row" justifyContent="center">
            <IconButton
              onClick={toggleTheme}
              sx={{
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' }
              }}
            >
              {isDark ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: { lg: drawerWidth },
          flexShrink: { lg: 0 },
          bgcolor: isDark ? '#0f0f0f' : '#f8fafc',
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              bgcolor: isDark ? '#0f0f0f' : '#f8fafc',
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', lg: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              position: 'relative',
              bgcolor: isDark ? '#0f0f0f' : '#f8fafc',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: isDark ? '#0f0f0f' : '#f8fafc',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          p: isMobile ? 0 : 3,
          borderRadius: isMobile ? 0 : 3,
        }}
      >
        {/* Top Header - Mobile only */}
        {isMobile && (
          <Box sx={{ mb: 2, mt: 2, px: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <IconButton
                onClick={handleDrawerToggle}
                sx={{
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': { boxShadow: 2 }
                }}
              >
                <MenuIcon />
              </IconButton>

              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton
                  component={Link}
                  to="/notifications"
                  sx={{
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { boxShadow: 2 }
                  }}
                >
                  <Badge badgeContent={!isEmailVerified ? 1 : 0} color="warning">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>

                <Avatar
                  onClick={handleUserMenuOpen}
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: 'primary.main',
                    cursor: 'pointer',
                    boxShadow: 1,
                    '&:hover': { boxShadow: 2 }
                  }}
                >
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </Avatar>
              </Stack>
            </Stack>
          </Box>
        )}

        {/* Content Container with rounded borders */}
        <Box
          sx={{
            flexGrow: 1,
            bgcolor: 'background.paper',
            boxShadow: isDark ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: isDark ? `1px solid ${theme.palette.divider}` : 'none',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            p: isMobile ? 0 : 3,
            borderRadius: isMobile ? 0 : 3,
          }}
        >
          {/* Desktop Header - Inside content container */}
          {!isMobile && (
            <Box
              sx={{
                p: 4,
                pb: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: 'transparent',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" fontWeight={600} sx={{ mb: 0.5 }}>
                    {getPageTitle()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gestiona y controla tus tarjetas digitales
                  </Typography>
                </Box>

                <Stack direction="row" alignItems="center" spacing={2}>
                  {/* Notifications */}
                  <IconButton
                    component={Link}
                    to="/notifications"
                    sx={{
                      bgcolor: isDark ? 'action.hover' : '#f1f5f9',
                      '&:hover': {
                        bgcolor: isDark ? 'action.selected' : '#e2e8f0'
                      }
                    }}
                  >
                    <Badge badgeContent={!isEmailVerified ? 1 : 0} color="warning">
                      <NotificationsIcon />
                    </Badge>
                  </IconButton>

                  {/* User Avatar */}
                  <Avatar
                    onClick={handleUserMenuOpen}
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: 'primary.main',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 2,
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </Avatar>
                </Stack>
              </Stack>
            </Box>
          )}

          {/* Content */}
          <Box sx={{
            flexGrow: 1,
            overflow: 'auto',
            p: 0, // Remove padding here since children will handle their own padding
          }}>
            {children}
          </Box>
        </Box>
      </Box>

      {/* User Menu */}
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleUserMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          elevation: 8,
          sx: {
            borderRadius: 2,
            minWidth: 200,
            mt: 1,
          }
        }}
      >
        <MenuItem onClick={() => { navigate('/settings'); handleUserMenuClose(); }} sx={{ display: 'none' }}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Configuración</ListItemText>
        </MenuItem>
        <Divider sx={{ display: 'none' }}/>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cerrar Sesión</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default DashboardLayout; 